import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, Check, MapPin, ArrowRight, Shield, Star } from 'lucide-react';

const benefits = [
  { icon: <Zap className="w-5 h-5 text-yellow-600" />, title: 'Bis zu 400€ sparen', text: 'Durch einen Wechsel des Stromanbieters können Sie hunderte Euro im Jahr sparen.' },
  { icon: <Shield className="w-5 h-5 text-blue-600" />, title: 'Preisgarantie', text: 'Schützen Sie sich vor Preiserhöhungen mit einer Preisgarantie.' },
  { icon: <Star className="w-5 h-5 text-green-600" />, title: 'Ökostrom', text: 'Günstiger und umweltfreundlich – Ökostrom ist oft günstiger als konventioneller Strom.' },
  { icon: <Check className="w-5 h-5 text-blue-600" />, title: 'Einfacher Wechsel', text: 'Der neue Anbieter kümmert sich um die Kündigung Ihres alten Vertrags.' },
];

const faqs = [
  {
    q: 'Wie kann ich in Dillingen Strom wechseln?',
    a: 'Ganz einfach: Vergleichen Sie die Tarife mit unserem Rechner, wählen Sie den günstigsten und beauftragen Sie den Wechsel. Der neue Anbieter kümmert sich um alles Weitere.',
  },
  {
    q: 'Wie viel kann ich beim Stromwechsel in Dillingen sparen?',
    a: 'Je nach Verbrauch und aktuellem Tarif können Sie bis zu 400€ im Jahr sparen. Ein Vergleich lohnt sich immer.',
  },
  {
    q: 'Gibt es Ökostrom in Dillingen?',
    a: 'Ja, es gibt viele Ökostrom-Tarife in Dillingen. Diese sind oft günstiger als konventioneller Strom und umweltfreundlich.',
  },
  {
    q: 'Bekomme ich eine Preisgarantie?',
    a: 'Viele Tarife bieten eine Preisgarantie, die Sie vor Preiserhöhungen schützt. Wir helfen Ihnen, den richtigen Tarif zu finden.',
  },
];

const StromDillingenPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Strom Dillingen - Stromtarife vergleichen & sparen | Easy Vertrag</title>
        <meta name="description" content="Strom in Dillingen vergleichen und bis zu 400€ sparen. Ökostrom, Preisgarantie und einfacher Wechsel. Kostenlos beraten lassen!" />
        <meta name="keywords" content="Strom Dillingen, Stromtarif Dillingen, Ökostrom Dillingen, Stromanbieter Saarland, Energie vergleichen" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/strom-dillingen" />
        <meta property="og:title" content="Strom Dillingen - Stromtarife vergleichen | Easy Vertrag" />
        <meta property="og:description" content="Strom in Dillingen vergleichen und bis zu 400€ sparen. Kostenlos beraten lassen!" />
        <meta property="og:url" content="https://easy4vertrag.de/strom-dillingen" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs.map(f => `{
                "@type": "Question",
                "name": "${f.q}",
                "acceptedAnswer": {"@type": "Answer", "text": "${f.a}"}
              }`).join(',')}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Strom Dillingen", "item": "https://easy4vertrag.de/strom-dillingen"}
            ]
          }
        `}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-yellow-600 to-orange-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-15 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Dillingen · Saarland
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Strom in Dillingen vergleichen
            </h1>
            <p className="text-xl text-orange-100 mb-4">
              Wechseln Sie Ihren Stromanbieter und sparen Sie bis zu 400€ im Jahr. Kostenlos und unverbindlich.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/strom-gas" className="inline-flex items-center justify-center px-7 py-3 bg-white text-orange-700 font-semibold rounded-lg hover:bg-orange-50 transition-colors">
                Strom vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a href="https://wa.me/491797473491" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-orange-700 transition-colors">
                WhatsApp Beratung
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">Warum Strom wechseln in Dillingen?</h2>
            <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
              Ein Stromanbieterwechsel ist einfach, kostenlos und kann Ihnen hunderte Euro im Jahr sparen.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{b.icon}</div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">{b.title}</h3>
                    <p className="text-gray-600 text-sm">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">Häufige Fragen zu Strom in Dillingen</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-2 text-base">{faq.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-orange-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Jetzt Strom in Dillingen vergleichen</h2>
            <p className="text-orange-100 mb-8">Kostenlose Beratung – wir finden den günstigsten Stromanbieter für Sie.</p>
            <Link to="/strom-gas" className="inline-flex items-center px-8 py-3 bg-white text-orange-600 font-bold rounded-lg hover:bg-orange-50 transition-colors">
              Strom vergleichen <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StromDillingenPage;
